import * as React from "react";
import { useEffect, useRef } from "react";
import { Control, UseFormRegister, UseFormWatch, UseFormSetValue, UseFormTrigger, FieldErrors } from "react-hook-form";
import { DndProvider, useDrag, useDrop, useDragLayer } from "react-dnd";
import { TouchBackend } from "react-dnd-touch-backend";
import styles from "./styles.module.scss";

const ItemTypes = {
  RANKING_ITEM: 'ranking_item',
};

export interface RankingItem {
  id: string;
  text: string;
  color: string;
  position: number;
}

interface RankingFieldProps {
  control: Control<any>;
  register: UseFormRegister<any>;
  watch: UseFormWatch<any>;
  setValue: UseFormSetValue<any>;
  trigger: UseFormTrigger<any>;
  errors: FieldErrors<any>;
  name: string;
  questionText: string;
  questionNumber: number;
  containerClassName?: string;
  titleClassName?: string;
  subtitleClassName?: string;
  items: RankingItem[];
}

interface DragItemProps {
  item: RankingItem;
  index: number;
  moveItem: (fromIndex: number, toIndex: number) => void;
  isRanked?: boolean;
  onTap?: () => void;
}

const DragItem: React.FC<DragItemProps> = ({ item, index, moveItem, isRanked, onTap }) => {
  const ref = useRef<HTMLDivElement>(null);

  const [{ isDragging }, drag] = useDrag({
    type: ItemTypes.RANKING_ITEM,
    item: () => ({ index, item, isRanked }),
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  // Only enable drop for available items, not ranked items
  const [, drop] = useDrop({
    accept: ItemTypes.RANKING_ITEM,
    hover: (dragItem: { index: number; isRanked: boolean }) => {
      if (!isRanked && dragItem.index !== index) {
        moveItem(dragItem.index, index);
        dragItem.index = index;
      }
    },
  });

  if (isRanked) {
    drag(ref);
  } else {
    drag(drop(ref));
  }

  return (
    <div
      ref={ref}
      className={`${styles.rankingCard} ${isDragging ? styles.cardDragging : ''}`}
      style={{
        backgroundColor: item.color,
        cursor: onTap ? 'pointer' : undefined,
      }}
      // A quick tap (below the drag backend's delay) ranks/un-ranks the item, so
      // touch candidates aren't forced to drag into the numbered slots.
      onClick={onTap}
      title={onTap ? (isRanked ? 'Tap to remove' : 'Tap to place in the next slot') : undefined}
    >
      <span className={styles.cardText}>{item.text}</span>
    </div>
  );
};

const CustomDragLayer: React.FC = () => {
  const { item, isDragging, currentOffset } = useDragLayer((monitor) => ({
    item: monitor.getItem() as { item: RankingItem } | null,
    isDragging: monitor.isDragging(),
    currentOffset: monitor.getClientOffset(),
  }));

  if (!isDragging || !currentOffset || !item?.item) {
    return null;
  }

  return (
    <div className={styles.dragLayerContainer}>
      <div
        className={styles.dragPreview}
        style={{
          transform: `translate(${currentOffset.x - 40}px, ${currentOffset.y - 20}px)`,
          backgroundColor: item.item.color,
        }}
      >
        <span className={styles.cardText}>{item.item.text}</span>
      </div>
    </div>
  );
};

interface DropZoneProps {
  position: number;
  hasItem: boolean;
  onDrop: (item: RankingItem, position: number) => void;
  children?: React.ReactNode;
}

const DropZone: React.FC<DropZoneProps> = ({ position, hasItem, onDrop, children }) => {
  const ref = useRef<HTMLDivElement>(null);

  const [{ isOver }, drop] = useDrop({
    accept: ItemTypes.RANKING_ITEM,
    drop: (dragItem: { item: RankingItem; isRanked: boolean }) => {
      onDrop(dragItem.item, position);
    },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
    }),
  });

  drop(ref);

  return (
    <div
      ref={ref}
      className={`${styles.dropZone} ${isOver ? styles.dropZoneHover : ''} ${hasItem ? styles.hasItem : ''}`}
      style={{ minHeight: '40px' }}
    >
      {children}
      {!hasItem && <span className={styles.positionNumber}>{position}</span>}
    </div>
  );
};

const RankingField: React.FC<RankingFieldProps> = ({
  control,
  register,
  watch,
  setValue,
  trigger,
  errors,
  name,
  questionText,
  questionNumber,
  containerClassName,
  titleClassName,
  subtitleClassName,
  items: initialItems,
}) => {
  const watchedValue = watch(name);
  const [isInitialized, setIsInitialized] = React.useState(false);

  // Initialize state from watched value or defaults
  const initializeFromValue = React.useCallback((value: any) => {
    if (value && Array.isArray(value) && value.some(item => item !== null)) {
      // Filter out persisted ranked items and available items
      const ranked = value.filter((item: any) => item !== null);
      const rankedIds = ranked.map((item: any) => item.id);
      const available = initialItems.filter(item => !rankedIds.includes(item.id));
      return { ranked: value, available };
    }
    return { ranked: Array(initialItems.length).fill(null), available: initialItems };
  }, [initialItems]);

  const [availableItems, setAvailableItems] = React.useState<RankingItem[]>(() => 
    initializeFromValue(watchedValue).available
  );
  const [rankedItems, setRankedItems] = React.useState<(RankingItem | null)[]>(() => 
    initializeFromValue(watchedValue).ranked
  );

  // Hydrate from form value on mount or when watchedValue changes initially
  useEffect(() => {
    if (!isInitialized && watchedValue) {
      const { ranked, available } = initializeFromValue(watchedValue);
      setRankedItems(ranked);
      setAvailableItems(available);
      setIsInitialized(true);
    } else if (!isInitialized) {
      setValue(name, Array(initialItems.length).fill(null));
      setIsInitialized(true);
    }
  }, [watchedValue, isInitialized, setValue, name, initializeFromValue]);

  // Remove effect that causes infinite loop. Instead, update form value only on user actions.

  const moveItemFromAvailable = (fromIndex: number, toIndex: number) => {
    const newAvailable = [...availableItems];
    const [movedItem] = newAvailable.splice(fromIndex, 1);
    newAvailable.splice(toIndex, 0, movedItem);
    setAvailableItems(newAvailable);
    setValue(name, rankedItems, { shouldDirty: true });
    trigger(name);
  }

  const moveItemFromRanked = (fromIndex: number, toIndex: number) => {
    const newRanked = [...rankedItems];
    const [movedItem] = newRanked.splice(fromIndex, 1);
    newRanked.splice(toIndex, 0, movedItem);

    for (let i = 0; i < newRanked.length; i++) {
      if (newRanked[i] === undefined) {
        newRanked[i] = null;
      }
    }

    setRankedItems(newRanked);
    setValue(name, newRanked, { shouldDirty: true });
    trigger(name);
  };

  const handleItemDrop = (item: RankingItem, targetPosition: number) => {
    const targetIndex = targetPosition - 1;

    // Check if item is already in rankedItems
    const rankedIndex = rankedItems.findIndex(rankedItem => rankedItem?.id === item.id);
    const isRanked = rankedIndex !== -1;

    if (isRanked) {
      // Move within rankedItems
      if (rankedIndex !== targetIndex) {
        moveItemFromRanked(rankedIndex, targetIndex);
      }
      return;
    }

    // Remove from availableItems
    const availableIndex = availableItems.findIndex(availableItem => availableItem.id === item.id);
    if (availableIndex !== -1) {
      const newAvailable = availableItems.filter(availableItem => availableItem.id !== item.id);
      setAvailableItems(newAvailable);
    }

    // Place in rankedItems
    const newRanked = [...rankedItems];
    const displacedItem = newRanked[targetIndex];
    newRanked[targetIndex] = item;

    setRankedItems(newRanked);
    setValue(name, newRanked, { shouldDirty: true });
    trigger(name);

    // If there's a displaced item, add it back to availableItems
    if (displacedItem) {
      // Prevent duplicates in availableItems
      if (!availableItems.some(avail => avail.id === displacedItem.id)) {
        setAvailableItems(prev => [...prev, displacedItem]);
        setValue(name, newRanked, { shouldDirty: true });
        trigger(name);
      }
    }
  };

  // Tap fallbacks for touch devices (drag-and-drop is unreliable on mobile).
  const placeInNextEmptySlot = (item: RankingItem) => {
    const emptyIndex = rankedItems.findIndex((ranked) => ranked === null);
    if (emptyIndex === -1) return; // all slots filled
    handleItemDrop(item, emptyIndex + 1);
  };

  const removeRankedItem = (item: RankingItem) => {
    const rankedIndex = rankedItems.findIndex((ranked) => ranked?.id === item.id);
    if (rankedIndex === -1) return;

    const newRanked = [...rankedItems];
    newRanked[rankedIndex] = null;
    setRankedItems(newRanked);
    setValue(name, newRanked, { shouldDirty: true });
    trigger(name);

    if (!availableItems.some((avail) => avail.id === item.id)) {
      setAvailableItems((prev) => [...prev, item]);
    }
  };

  const getErrorMessage = () => {
    const error = errors[name];
    if (error && typeof error.message === 'string') {
      return error.message;
    }
    return '';
  };

  return (
    <DndProvider backend={TouchBackend} options={{ enableMouseEvents: true, delayTouchStart: 150 }}>
      <CustomDragLayer />
      <div className={`${styles.container} ${containerClassName || ''}`}>
        <div className={styles.questionHeader}>
          <h3 className={`${styles.questionTitle} ${titleClassName || ''}`}>
            {questionNumber}. {questionText}
          </h3>
        </div>

        <div className={styles.rankingArea}>
          <div className={styles.availableItems}>
            {availableItems.map((item, index) => (
              <DragItem
                key={item.id}
                item={item}
                index={index}
                moveItem={moveItemFromAvailable}
                isRanked={false}
                onTap={() => placeInNextEmptySlot(item)}
              />
            ))}
          </div>

          <div className={`${styles.instructionText} ${subtitleClassName || ''}`}>
            Drag and drop, or tap, a selection to place it in your desired position.
          </div>

          <div className={styles.dropZones}>
            {Array.from({ length: initialItems.length }, (_, index) => {
              const position = index + 1;
              const item = rankedItems[index];

              return (
                <DropZone
                  key={position}
                  position={position}
                  hasItem={!!item}
                  onDrop={(droppedItem) => handleItemDrop(droppedItem, position)}
                >
                  {item && (
                    <DragItem
                      item={item}
                      index={index}
                      moveItem={moveItemFromRanked}
                      isRanked={true}
                      onTap={() => removeRankedItem(item)}
                    />
                  )}
                </DropZone>
              );
            })}
          </div>
        </div>

        {getErrorMessage() && (
          <div className={styles.errorMessage}>
            {getErrorMessage()}
          </div>
        )}

        <input
          type="hidden"
          {...register(name)}
          value={JSON.stringify(rankedItems)}
        />
      </div>
    </DndProvider>
  );
};

export { RankingField };